import React, { useState, useEffect, useMemo, useRef } from 'react';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore'; 
import SystemCalculator from './SystemCalculator';
import { captureViewportRegion, toPrintFriendlyCanvas } from '../utils/captureViewportRegion';
// Sync-Status (Cloud/Verbunden) sitzt jetzt in App.tsx Header-Zeile
import { resolveDisplayMandantNr } from '../utils/clientIdentity';
import {
  formatMandantNrDisplay,
  resolveTarifBadgeDisplay,
  TARIF_BADGE_CLASS,
} from '../utils/mandantNrFormat';
import { MandantNrPill } from './MandantNrPill';
import { useCalculationsSelector } from '../store/appStore';
import { resolveTeamProThreshold } from '../utils/clientPricing';

// --- STYLES ---
const customStyles = `
  .custom-scrollbar::-webkit-scrollbar { width: 4px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); }
  .custom-scrollbar::-webkit-scrollbar-thumb { 
    background: rgba(230, 194, 153, 0.5);
    border-radius: 10px; 
  }

  /* --- HIGH-FIDELITY CARBON COMMAND CENTER INJECTION --- */
  .carbon-weave-dock {
    background-color: #0a0b0e;
    background-image: 
        radial-gradient(circle at 100% 150%, #08090c 24%, #14161f 24%, #14161f 28%, #08090c 28%, #08090c 36%, #14161f 36%, #14161f 40%, transparent 40%, transparent),
        radial-gradient(circle at 0 150%, #08090c 24%, #14161f 24%, #14161f 28%, #08090c 28%, #08090c 36%, #14161f 36%, #14161f 40%, transparent 40%, transparent),
        radial-gradient(circle at 50% 100%, #14161f 10%, #08090c 10%, #08090c 23%, #14161f 23%, #14161f 30%, #08090c 30%, #08090c 43%, #14161f 43%, #14161f 50%, #08090c 50%, #08090c 63%, #14161f 63%, #14161f 71%, transparent 71%, transparent),
        radial-gradient(circle at 100% 50%, #14161f 5%, #08090c 5%, #08090c 15%, #14161f 15%, #14161f 20%, #08090c 20%, #08090c 29%, #14161f 29%, #14161f 34%, #08090c 34%, #08090c 44%, #14161f 44%, #14161f 49%, transparent 49%, transparent),
        radial-gradient(circle at 0 50%, #14161f 5%, #08090c 5%, #08090c 15%, #14161f 15%, #14161f 20%, #08090c 20%, #08090c 29%, #14161f 29%, #14161f 34%, #08090c 34%, #08090c 44%, #14161f 44%, #14161f 49%, transparent 49%, transparent);
    background-size: 16px 16px;
  }

  .carbon-panel-dock {
    position: relative;
    border: 1px solid #20232b;
    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.7), inset 0 1px 1px rgba(255,255,255,0.05);
  }

  .carbon-panel-dock::after {
    content: ''; position: absolute; inset: 0; background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.4)); border-radius: inherit; pointer-events: none;
  }

  /* --- PREISEXPERT TOOLTIP SYSTEM --- */
  [data-pe-tooltip] { position: relative; }
  
  [data-pe-tooltip]:hover::after {
      content: attr(data-pe-tooltip);
      position: absolute;
      bottom: 110%; 
      left: 50%;
      transform: translateX(-50%);
      background-color: #E6C299; 
      color: #000000; 
      border: 1px solid #ffffff;
      padding: 6px 10px;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 800;
      white-space: nowrap;
      z-index: 9999;
      box-shadow: 0 4px 15px rgba(0,0,0,0.8);
      pointer-events: none;
      opacity: 0;
      animation: tooltipFadeIn 0.2s forwards;
  }

  [data-pe-tooltip]:hover::before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-width: 6px;
      border-style: solid;
      border-color: #E6C299 transparent transparent transparent;
      z-index: 9999;
      opacity: 0;
      animation: tooltipFadeIn 0.2s forwards;
  }

  @keyframes tooltipFadeIn {
      to { opacity: 1; transform: translateX(-50%) translateY(-5px); }
  }

  @media print {
      html, body {
          width: 100% !important;
          height: auto !important;
          overflow: visible !important;
          min-width: 0 !important;
          background-color: white !important;
          color: black !important;
       }

      .pe-productivity-dock, 
      .floating-action-bar,
      [data-pe-tooltip]::after, 
      [data-pe-tooltip]::before, 
      .pe-toast,
      .admin-dock-row {
          display: none !important;
      }

      table {
          min-width: 0 !important;
          width: 100% !important;
          page-break-inside: auto;
      }
      
      tr { page-break-inside: avoid; page-break-after: auto; }
      
      .table-wrapper {
          overflow: visible !important;
      }
      
      .btn-admin-lock, .admin-badge, .reset-btn-row {
          display: none !important;
      }

      .table-input, .table-select, .table-textarea, .table-calc {
          color: black !important;
          background: transparent !important;
      }
      .input-wrapper, .result-wrapper, .table-select {
          background: transparent !important;
          border: 1px solid #94a3b8 !important;
      }
      .text-\\[\\#E6C299\\] {
          color: black !important;
      }
  }
`;

// --- ICONS ---
const IconBase = ({ children, size = 20, className = "" }: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>
);
const Undo2 = (props: any) => <IconBase {...props}><path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"/></IconBase>;
const Redo2 = (props: any) => <IconBase {...props}><path d="m15 14 5-5-5-5"/><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5v0A5.5 5.5 0 0 0 9.5 20H13"/></IconBase>;
const History = (props: any) => <IconBase {...props}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></IconBase>;
const Lock = (props: any) => <IconBase {...props}><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></IconBase>;
const CalculatorIcon = (props: any) => <IconBase {...props}><rect width="16" height="20" x="4" y="2" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="16" y1="14" x2="16" y2="18"/><path d="M16 10h.01"/><path d="M12 10h.01"/><path d="M8 10h.01"/><path d="M12 14h.01"/><path d="M8 14h.01"/><path d="M12 18h.01"/><path d="M8 18h.01"/></IconBase>;
const Printer = (props: any) => <IconBase {...props}><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><path d="M6 14h12v8H6z"/></IconBase>;
const Camera = (props: any) => <IconBase {...props}><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></IconBase>;
const FunctionIcon = (props: any) => <IconBase {...props}><path d="M4 18h2c1 0 2-1 2-2V8c0-1 1-2 2-2h2"/><line x1="6" y1="12" x2="10" y2="12"/></IconBase>;
const Trash2 = (props: any) => <IconBase {...props}><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></IconBase>;
const X = (props: any) => <IconBase {...props}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></IconBase>;
const Pencil = (props: any) => <IconBase {...props}><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></IconBase>;
const Square = (props: any) => <IconBase {...props}><rect width="18" height="18" x="3" y="3" rx="2" /></IconBase>;
const Type = (props: any) => <IconBase {...props}><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" x2="15" y1="20" y2="20" /><line x1="12" x2="12" y1="4" y2="20" /></IconBase>;
const Download = (props: any) => <IconBase {...props}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></IconBase>;
const Copy = (props: any) => <IconBase {...props}><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></IconBase>;
const Scissors = (props: any) => <IconBase {...props}><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></IconBase>;
const ExternalLink = (props: any) => <IconBase {...props}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></IconBase>;


// --- REGULÄRER TOOLBAR BUTTON ---
const ToolbarButton = ({ icon: Icon, label, onClick, isActive, color = "text-slate-400", disabled = false, tooltip = "" }: any) => (
  <button 
    type="button" 
    onClick={onClick} 
    disabled={disabled} 
    className={`flex flex-col items-center justify-center w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-lg border bg-black/50 hover:bg-slate-800/80 transition-all duration-200 group relative z-10 shrink-0 touch-manipulation ${isActive ? 'border-[#E6C299] bg-black shadow-[0_0_12px_rgba(230,194,153,0.25)]' : 'border-[#22252e]'} ${disabled ? 'opacity-20 cursor-not-allowed' : ''}`} 
    data-pe-tooltip={tooltip || label}
  >
    <div className={`${isActive ? 'text-[#E6C299] led-gold' : color} mb-0.5 transform group-hover:scale-110 transition-transform`}><Icon size={18} className="md:w-5 md:h-5" /></div>
    <div className="flex flex-col items-center leading-none">
      <span className={`text-[7px] md:text-[8px] font-bold uppercase tracking-wide ${isActive ? 'text-[#E6C299]' : 'text-slate-500 group-hover:text-slate-300'}`}>{label}</span>
    </div>
  </button>
);

// --- DER "MINI" ADMIN BUTTON ---
const MiniAdminButton = ({ icon: Icon, label, onClick, isActive, color = "text-red-500/50", tooltip = "" }: any) => (
    <button 
      type="button" 
      onClick={onClick} 
      className={`flex flex-col items-center justify-center w-9 h-9 rounded-md border bg-black/70 hover:bg-slate-800/80 transition-all duration-200 group relative z-10 shrink-0 touch-manipulation ${isActive ? 'border-red-500/50 bg-black shadow-[0_0_8px_rgba(239,68,68,0.25)]' : 'border-[#22252e]'}`} 
      data-pe-tooltip={tooltip || label}
    >
      <div className={`${isActive ? 'text-[#E6C299]' : color} transform group-hover:scale-110 transition-transform`}><Icon size={14} /></div>
      <span className={`text-[5px] font-bold uppercase tracking-wider mt-0.5 ${isActive ? 'text-[#E6C299]' : 'text-slate-600 group-hover:text-slate-400'}`}>{label}</span>
    </button>
);

const DockInput = ({ value, onChange, type = 'text', placeholder = '', autoFocus = false, onKeyDown }: any) => (
  <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} onKeyDown={onKeyDown} className="w-full px-4 py-3 rounded-lg font-mono-numbers text-sm bg-black text-white border border-[#22252e] focus:border-[#E6C299] outline-none text-center relative z-10" />
);

const EDIT_COLORS = ['#ff4444', '#3b82f6', '#22c55e', '#E6C299', '#ffffff', '#000000'];

export default function ProductivityDock({
    showNotification,
    syncStatus: _syncStatus = 'idle',
    isAdminView = false,
    onToolEngage,
}: {
    showNotification: (msg: string, type: 'success' | 'error') => void;
    syncStatus?: 'idle' | 'syncing' | 'success' | 'error';
    isAdminView?: boolean;
    /** z. B. Mobile-Menü schließen, bevor Overlay/Rechner öffnet */
    onToolEngage?: () => void;
}) {
    const engage = () => { try { onToolEngage?.(); } catch { /* ignore */ } };

    const [activeTool, setActiveTool] = useState<string | null>(null);
    const [isAdminUnlocked, setIsAdminUnlocked] = useState(false);
    const [authPin, setAuthPin] = useState('');
    const [authError, setAuthError] = useState(false);
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(null);
    const [restorePin, setRestorePin] = useState('');
    const [restorePinError, setRestorePinError] = useState(false);

    const [photoStep, setPhotoStep] = useState<'idle' | 'selecting' | 'editing'>('idle');
    const [selectionType, setSelectionType] = useState<'photo' | 'print'>('photo');
    const selectionTypeRef = useRef<'photo' | 'print'>('photo');
    const [selection, setSelection] = useState({ startX: 0, startY: 0, endX: 0, endY: 0, isActive: false });
    const selectionRef = useRef(selection);
    selectionRef.current = selection;
    const [editImage, setEditImage] = useState<string | null>(null);
    const [editTool, setEditTool] = useState<'pen' | 'rect' | 'text'>('pen');
    const [editHistory, setEditHistory] = useState<string[]>([]); 
    
    const [editColor, setEditColor] = useState<string>('#ff4444');
    const [textInput, setTextInput] = useState<{ x: number, y: number, text: string, visible: boolean }>({ x: 0, y: 0, text: '', visible: false });
    
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textInputRef = useRef<HTMLInputElement>(null);
    const isDrawing = useRef(false);
    const startPos = useRef({ x: 0, y: 0 });
    const snapshotRef = useRef<ImageData | null>(null);

    useEffect(() => {
        if (textInput.visible && textInputRef.current) {
            setTimeout(() => {
                textInputRef.current?.focus();
            }, 50);
        }
    }, [textInput.visible]);

    const [calcPos, setCalcPos] = useState({ x: 0, y: 0 });
    const isDraggingCalc = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });

    const { undo, redo, past, future, savedSnapshots, createSnapshot, loadSnapshot, deleteSnapshot, showFormulas, setShowFormulas, settings, clientData, team } = useAppStore((state: any) => ({
        undo: state.undo, redo: state.redo, past: state.past, future: state.future,
        savedSnapshots: state.savedSnapshots, createSnapshot: state.createSnapshot,
        loadSnapshot: state.loadSnapshot, deleteSnapshot: state.deleteSnapshot,
        showFormulas: state.showFormulas, setShowFormulas: state.setShowFormulas,
        settings: state.settings,
        clientData: state.clientData,
        team: state.team
    }));

    const { crmClients } = useAdminStore();
    const godModeId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('GOD_MODE_ID') : null;
    const godModeClient = godModeId ? crmClients.find(c => c.id === godModeId) : null;
    const calculations = useCalculationsSelector();
    const liveMonatsumsatz = Number(calculations?.monatsumsatzNetto) || 0;
    const setClientData = useAppStore((s: any) => s.setClientData);

    // TeamPro → TeamPro+ live, sobald Netto-Monatsumsatz die Global-Schwelle erreicht
    useEffect(() => {
        if (!clientData || typeof setClientData !== 'function') return;
        if (clientData.isSolo === true || clientData.forceSoloTarif === true) return;

        const tier = String(clientData.packageTier || clientData.tarifStatus || '').trim();
        const isTeamProLine =
            clientData.hasEmployeeEval === true ||
            tier === 'TeamPro' ||
            tier === 'TeamPro+' ||
            clientData.insiderTeamleaderStandardChecked === true ||
            clientData.insiderTeamleaderPlusChecked === true ||
            clientData.isTeamProPlus === true;
        if (!isTeamProLine) return;

        const threshold =
            resolveTeamProThreshold(settings?.global) ||
            Number(settings?.global?.Grobkundenaufschlag_ab) ||
            0;
        if (threshold <= 0 || liveMonatsumsatz <= 0) return;

        const shouldPlus = liveMonatsumsatz >= threshold;
        const isPlusNow =
            clientData.isTeamProPlus === true ||
            clientData.insiderTeamleaderPlusChecked === true ||
            tier === 'TeamPro+';

        if (shouldPlus && !isPlusNow) {
            setClientData((prev: any) => ({
                ...prev,
                packageTier: 'TeamPro+',
                tarifStatus: 'TeamPro+',
                isTeamProPlus: true,
                insiderTeamleaderPlusChecked: true,
                insiderTeamleaderStandardChecked: false,
                hasEmployeeEval: true,
                isSolo: false,
                nettoMonatsumsatz: liveMonatsumsatz,
                ...(prev?.hasBranches
                    ? {}
                    : { aggregatedNettoMonatsumsatz: liveMonatsumsatz }),
            }));
        } else if (!shouldPlus && isPlusNow) {
            setClientData((prev: any) => ({
                ...prev,
                packageTier: 'TeamPro',
                tarifStatus: 'TeamPro',
                isTeamProPlus: false,
                insiderTeamleaderPlusChecked: false,
                insiderTeamleaderStandardChecked: true,
                nettoMonatsumsatz: liveMonatsumsatz,
                ...(prev?.hasBranches
                    ? {}
                    : { aggregatedNettoMonatsumsatz: liveMonatsumsatz }),
            }));
        }
    // Granulare Felder statt ganzem clientData — sonst Loop durch setClientData
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        clientData?.isSolo,
        clientData?.forceSoloTarif,
        clientData?.hasEmployeeEval,
        clientData?.packageTier,
        clientData?.tarifStatus,
        clientData?.isTeamProPlus,
        clientData?.insiderTeamleaderPlusChecked,
        clientData?.insiderTeamleaderStandardChecked,
        clientData?.hasBranches,
        liveMonatsumsatz,
        settings?.global,
        setClientData,
    ]);

    const currentYearStorage = typeof window !== 'undefined' ? localStorage.getItem('CURRENT_YEAR') : null;
    const fiscalYear = currentYearStorage || settings?.global?.fiscalYear || new Date().getFullYear();

    const activeBetreuer = godModeClient?.betreuer || clientData?.betreuer || "";

    const rawMandantNr = godModeClient?.mandantNr || godModeClient?.clientData?.mandantNr || clientData?.mandantNr || "";
    
    const displayMandantNr = useMemo(
        () => formatMandantNrDisplay(resolveDisplayMandantNr(rawMandantNr, activeBetreuer, team)),
        [rawMandantNr, activeBetreuer, team]
    );

    const tarifBadge = useMemo(
        () => {
            // Store (clientData) schlägt God-Mode-CRM-Snapshot: Filial-/Plus-Flags liegen nur im Sync-Store
            const src = {
                ...(godModeClient || {}),
                ...(clientData || {}),
                hasBranches: clientData?.hasBranches ?? godModeClient?.hasBranches,
                branches: clientData?.branches ?? godModeClient?.branches,
                isTeamProPlus: clientData?.isTeamProPlus ?? godModeClient?.isTeamProPlus,
                insiderTeamleaderPlusChecked:
                    clientData?.insiderTeamleaderPlusChecked ?? godModeClient?.insiderTeamleaderPlusChecked,
                aggregatedNettoMonatsumsatz:
                    clientData?.aggregatedNettoMonatsumsatz ?? godModeClient?.aggregatedNettoMonatsumsatz,
                nettoMonatsumsatz: clientData?.nettoMonatsumsatz ?? godModeClient?.nettoMonatsumsatz,
                hasEmployeeEval: clientData?.hasEmployeeEval ?? godModeClient?.hasEmployeeEval,
                isSolo: clientData?.isSolo ?? godModeClient?.isSolo,
                forceSoloTarif: clientData?.forceSoloTarif ?? godModeClient?.forceSoloTarif,
            };
            return resolveTarifBadgeDisplay({
                mandantNr: rawMandantNr,
                client: src,
                settings,
                isLegacy: !!(src?.isLegacy || src?.clientData?.isLegacy),
                isSolo:
                    src?.isSolo === true
                        ? true
                        : src?.isSolo === false
                          ? false
                          : undefined,
                hasEmployeeEval:
                    src?.hasEmployeeEval === true
                        ? true
                        : src?.hasEmployeeEval === false
                          ? false
                          : undefined,
                forceSoloTarif: !!src?.forceSoloTarif,
                // Live-BWA-Monatsumsatz hat Vorrang (sticky 0 blockiert sonst das Plus)
                nettoMonatsumsatz:
                    liveMonatsumsatz > 0
                        ? liveMonatsumsatz
                        : (Number(src?.aggregatedNettoMonatsumsatz) > 0
                            ? Number(src.aggregatedNettoMonatsumsatz)
                            : (Number(src?.nettoMonatsumsatz) > 0
                                ? Number(src.nettoMonatsumsatz)
                                : null)),
            });
        },
        [rawMandantNr, godModeClient, clientData, settings, liveMonatsumsatz]
    );

    const startSelection = (type: 'photo' | 'print') => { 
        engage();
        selectionTypeRef.current = type;
        setSelectionType(type);
        setPhotoStep('selecting'); 
        const cleared = { startX: 0, startY: 0, endX: 0, endY: 0, isActive: false };
        selectionRef.current = cleared;
        setSelection(cleared); 
    };

    const pointFromEvent = (e: { clientX: number; clientY: number }) => ({
        x: e.clientX,
        y: e.clientY,
    });
    
    const handleSelectionPointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
        const p = pointFromEvent(e);
        const next = { startX: p.x, startY: p.y, endX: p.x, endY: p.y, isActive: true };
        selectionRef.current = next;
        setSelection(next);
    };
    const handleSelectionPointerMove = (e: React.PointerEvent) => {
        if (!selectionRef.current.isActive) return;
        e.preventDefault();
        const p = pointFromEvent(e);
        const next = { ...selectionRef.current, endX: p.x, endY: p.y };
        selectionRef.current = next;
        setSelection(next);
    };
    
    const handleSelectionPointerUp = async (e?: React.PointerEvent) => {
        if (e) {
            try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        }
        const sel = selectionRef.current;
        if (!sel.isActive) return;
        const x = Math.min(sel.startX, sel.endX); 
        const y = Math.min(sel.startY, sel.endY);
        const w = Math.abs(sel.startX - sel.endX); 
        const h = Math.abs(sel.startY - sel.endY);

        const cleared = { ...sel, isActive: false };
        selectionRef.current = cleared;
        setSelection(cleared);
        
        if (w < 10 || h < 10) { setPhotoStep('idle'); return; }
        
        const currentType = selectionTypeRef.current;
        setPhotoStep('idle'); 
        
        showNotification(currentType === 'photo' ? "📸 Bereich wird erfasst..." : "🖨️ Druckbereich wird aufbereitet...", 'success');
        
        setTimeout(async () => {
            try {
                const isDark = document.documentElement.classList.contains('dark');
                const captureBg = isDark ? '#08090b' : '#D8D3CC';
                const canvas = await captureViewportRegion({
                    x,
                    y,
                    width: w,
                    height: h,
                    scale: currentType === 'print' ? 2 : 1,
                    backgroundColor: captureBg,
                });
                
                if (currentType === 'photo') {
                    setEditImage(canvas.toDataURL()); 
                    setPhotoStep('editing'); 
                    setEditHistory([]);
                } else if (currentType === 'print') {
                    // Negativ für Papier: heller Grund, dunkle Schrift — danach Popup schließen
                    const printCanvas = toPrintFriendlyCanvas(canvas, isDark);
                    const imgData = printCanvas.toDataURL('image/png');
                    const printWindow = window.open('', '_blank');
                    if (printWindow) {
                        printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Teildruck - PreisExpert</title>
  <style>
    @page { size: landscape; margin: 10mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0; padding: 0;
      background: #ffffff;
      color: #111;
    }
    body {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding: 12px;
      gap: 10px;
    }
    .toolbar {
      display: flex;
      gap: 8px;
      align-items: center;
      font-family: system-ui, sans-serif;
      font-size: 13px;
    }
    .toolbar button {
      cursor: pointer;
      border: 1px solid #ccc;
      background: #f4f4f4;
      border-radius: 6px;
      padding: 6px 12px;
      font-weight: 700;
    }
    .toolbar button.primary {
      background: #1a1a1a;
      color: #fff;
      border-color: #1a1a1a;
    }
    figure {
      margin: 0;
      max-width: 100%;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    img {
      display: block;
      max-width: 100%;
      max-height: calc(100vh - 64px);
      width: auto;
      height: auto;
      object-fit: contain;
      background: #fff;
    }
    @media print {
      body { padding: 0; min-height: 0; }
      .toolbar { display: none !important; }
      figure { max-width: 100%; }
      img { max-height: 100vh; max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button type="button" class="primary" id="pe-print-btn">Drucken</button>
    <button type="button" id="pe-close-btn">Schließen / Zurück</button>
    <span>Vorschau · bei Abbruch einfach „Schließen“ tippen</span>
  </div>
  <figure><img id="pe-print-img" alt="Teildruck" src="${imgData}" /></figure>
  <script>
    (function () {
      var closed = false;
      function goAway() {
        if (closed) return;
        closed = true;
        try { window.close(); } catch (e) {}
      }
      function doPrint() {
        try { window.focus(); window.print(); } catch (e) {}
      }
      window.addEventListener('afterprint', function () {
        setTimeout(goAway, 150);
      });
      document.getElementById('pe-print-btn').onclick = doPrint;
      document.getElementById('pe-close-btn').onclick = goAway;
      document.getElementById('pe-print-img').onload = function () {
        setTimeout(doPrint, 250);
      };
      // Sicherheitsnetz: Fenster nicht ewig offen lassen, falls afterprint ausbleibt
      setTimeout(function () {
        if (!closed) {
          /* Absicht: nicht auto-schließen vor dem Dialog — nur Hinweis bleibt */
        }
      }, 500);
    })();
  </script>
</body>
</html>`);
                        printWindow.document.close();
                    } else {
                        showNotification("Popup blockiert — bitte Popups für Teildruck erlauben.", "error");
                    }
                }
            } catch (err) {
                console.error(err);
                showNotification("Fehler bei der Bereichserfassung!", "error");
            }
        }, 150);
    };

    const saveToEditHistory = () => { if (canvasRef.current) setEditHistory(prev => [...prev, canvasRef.current!.toDataURL()]); };

    const undoPhotoEdit = () => {
        if (textInput.visible) {
            setTextInput((ti) => ({ ...ti, visible: false, text: '' }));
            return;
        }
        setEditHistory((prev) => {
            if (prev.length <= 1) return prev;
            const next = prev.slice(0, -1);
            const imgData = next[next.length - 1];
            if (imgData) {
                const img = new Image();
                img.onload = () => {
                    if (!canvasRef.current) return;
                    const c = canvasRef.current.getContext('2d');
                    if (!c) return;
                    c.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                    c.drawImage(img, 0, 0);
                };
                img.src = imgData;
            }
            return next;
        });
    };
    const undoPhotoEditRef = useRef(undoPhotoEdit);
    undoPhotoEditRef.current = undoPhotoEdit;

    useEffect(() => {
        if (photoStep === 'editing' && editImage && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                const img = new Image();
                img.onload = () => {
                    if(canvasRef.current) {
                        canvasRef.current.width = img.width; canvasRef.current.height = img.height;
                        ctx.drawImage(img, 0, 0); setEditHistory([canvasRef.current.toDataURL()]);
                    }
                };
                img.src = editImage;
            }
        }
    }, [photoStep, editImage]);

    useEffect(() => {
        if (photoStep !== 'editing') return;
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                undoPhotoEditRef.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [photoStep]);

    const commitText = () => {
        if (textInput.text.trim() !== '' && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                const rect = canvasRef.current.getBoundingClientRect();
                const scaleX = canvasRef.current.width / (rect.width || 1);
                const scaleY = canvasRef.current.height / (rect.height || 1);
                ctx.font = 'bold 24px sans-serif';
                ctx.fillStyle = editColor;
                ctx.textBaseline = 'top';
                ctx.fillText(textInput.text, textInput.x * scaleX, textInput.y * scaleY);
                saveToEditHistory();
            }
        }
        setTextInput({ ...textInput, visible: false, text: '' });
    };

    const finalizePhoto = (action: 'copy' | 'save') => {
        if (textInput.visible) commitText(); 

        if (!canvasRef.current) return;
        canvasRef.current.toBlob((blob) => {
            if (blob) {
                if (action === 'copy') { navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]); showNotification("✅ Kopiert!", 'success'); } 
                else { const link = document.createElement('a'); link.download = `Screenshot.png`; link.href = canvasRef.current!.toDataURL(); link.click(); }
                setPhotoStep('idle');
            }
        });
    };

    const handleCalcDragStart = (e: React.MouseEvent) => {
        isDraggingCalc.current = true;
        dragStart.current = { x: e.clientX - calcPos.x, y: e.clientY - calcPos.y };
    };

    useEffect(() => {
        const handleMove = (e: MouseEvent) => {
            if (!isDraggingCalc.current) return;
            setCalcPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
        };
        const handleUp = () => { isDraggingCalc.current = false; };
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
    }, [calcPos]);

    const handleUnlock = () => {
        if (authPin === '90') { 
            setIsAdminUnlocked(true); 
            if (pendingAction === 'formulas') setShowFormulas(true);
            if (pendingAction === 'preise') window.open('https://www.friseur-coaching.de/tools/Preisübersicht.html', '_blank');
            setActiveTool(pendingAction === 'history' ? 'history' : null);
            setAuthError(false); setAuthPin(''); setPendingAction(null);
        } else { setAuthError(true); setAuthPin(''); }
    };

    const handleRestoreSnapshot = (id: string) => {
        loadSnapshot(id);
        showNotification('Speicherstand wiederhergestellt', 'success');
    };

    const cancelRestoreConfirm = () => {
        setPendingRestoreId(null);
        setRestorePin('');
        setRestorePinError(false);
    };

    const handleConfirmRestore = () => {
        if (!pendingRestoreId) return;
        if (restorePin === '90') {
            handleRestoreSnapshot(pendingRestoreId);
            setPendingRestoreId(null);
            setRestorePin('');
            setRestorePinError(false);
        } else {
            setRestorePinError(true);
            setRestorePin('');
        }
    };

    const closeHistoryPanel = () => {
        setActiveTool(null);
        cancelRestoreConfirm();
    };

    return (
        <div className={`flex relative z-40 w-full md:w-auto ${isAdminView ? 'flex-col md:flex-row md:items-center gap-2' : 'flex-col items-stretch md:items-end'}`}>
            <style>{customStyles}</style>

            {/* SCREENSHOT OVERLAYS */}
            {photoStep === 'selecting' && (
                <div
                    className="fixed inset-0 z-[100000] bg-black/60 cursor-crosshair touch-none select-none"
                    onPointerDown={handleSelectionPointerDown}
                    onPointerMove={handleSelectionPointerMove}
                    onPointerUp={handleSelectionPointerUp}
                    onPointerCancel={handleSelectionPointerUp}
                >
                    <div
                        className="absolute border-2 border-dashed border-white bg-white/10 pointer-events-none"
                        style={{
                            left: Math.min(selection.startX, selection.endX),
                            top: Math.min(selection.startY, selection.endY),
                            width: Math.abs(selection.startX - selection.endX),
                            height: Math.abs(selection.startY - selection.endY),
                        }}
                    />
                    <p className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-xs font-bold uppercase tracking-widest bg-black/70 px-3 py-1.5 rounded-full pointer-events-none">
                        Bereich aufziehen · Finger oder Maus
                    </p>
                </div>
            )}

            {photoStep === 'editing' && (
                <div className="fixed inset-0 z-[10001] bg-black/95 flex flex-col items-center justify-center p-4">
                    <div className="bg-black rounded-2xl border border-[#2a2a2a] carbon-panel carbon-weave-bg flex flex-col max-w-full max-h-full overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.9)]">
                        <div className="p-4 border-b border-[#2a2a2a] flex items-center justify-between bg-black/40 carbon-content">
                            <div className="flex gap-2 items-center">
                                <button onClick={() => setEditTool('pen')} className={`p-2 rounded transition-colors ${editTool === 'pen' ? 'bg-slate-800 text-white shadow-inner border border-slate-700' : 'text-slate-400 hover:text-white'}`} data-pe-tooltip="Stift"><Pencil size={20}/></button>
                                <button onClick={() => setEditTool('rect')} className={`p-2 rounded transition-colors ${editTool === 'rect' ? 'bg-slate-800 text-white shadow-inner border border-slate-700' : 'text-slate-400 hover:text-white'}`} data-pe-tooltip="Rahmen"><Square size={20}/></button>
                                <button onClick={() => setEditTool('text')} className={`p-2 rounded transition-colors ${editTool === 'text' ? 'bg-slate-800 text-white shadow-inner border border-slate-700' : 'text-slate-400 hover:text-white'}`} data-pe-tooltip="Textfeld"><Type size={20}/></button>
                                <button
                                    type="button"
                                    onClick={undoPhotoEdit}
                                    disabled={editHistory.length <= 1 && !textInput.visible}
                                    className={`p-2 rounded transition-colors flex items-center gap-1.5 ${editHistory.length <= 1 && !textInput.visible ? 'text-slate-600 cursor-not-allowed opacity-40' : 'text-slate-300 hover:text-white hover:bg-slate-800 border border-transparent hover:border-slate-700'}`}
                                    data-pe-tooltip="Rückgängig"
                                    title="Rückgängig"
                                >
                                    <Undo2 size={20} />
                                    <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">Rückgängig</span>
                                </button>
                                <div className="flex gap-2 ml-4 pl-4 border-l border-[#2a2a2a]">
                                    {EDIT_COLORS.map(color => (
                                        <button key={color} onClick={() => setEditColor(color)} data-pe-tooltip="Farbe wählen" className={`w-6 h-6 rounded-full border-2 transition-all ${editColor === color ? 'border-white scale-125 shadow-[0_0_10px_rgba(255,255,255,0.5)]' : 'border-transparent hover:scale-110 opacity-70 hover:opacity-100'}`} style={{ backgroundColor: color }} />
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-3 items-center">
                                <button onClick={() => setPhotoStep('idle')} className="text-xs font-bold text-slate-400 uppercase hover:text-white transition-colors">Abbrechen</button>
                                <button onClick={() => finalizePhoto('copy')} className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded font-bold text-xs hover:bg-slate-700 transition-colors"><Copy size={16}/> Kopieren</button>
                                <button onClick={() => finalizePhoto('save')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 border border-blue-600 text-white rounded font-bold text-xs hover:bg-blue-600 transition-colors"><Download size={16}/> Speichern</button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto p-4 bg-[#050505] flex items-center justify-center relative carbon-content">
                            <div className="relative inline-block shadow-2xl">
                                <canvas ref={canvasRef}
                                    className={`${editTool === 'text' ? 'cursor-text' : 'cursor-crosshair'} bg-white rounded-sm touch-none`}
                                    onPointerDown={(e) => {
                                        const rect = canvasRef.current!.getBoundingClientRect();
                                        const scaleX = canvasRef.current!.width / rect.width;
                                        const scaleY = canvasRef.current!.height / rect.height;
                                        const displayX = e.clientX - rect.left;
                                        const displayY = e.clientY - rect.top;
                                        const x = displayX * scaleX;
                                        const y = displayY * scaleY;
                                        try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
                                        if (editTool === 'text') {
                                            if (!textInput.visible) { setTextInput({ x: displayX, y: displayY, text: '', visible: true }); } else { commitText(); }
                                            return;
                                        }
                                        if (textInput.visible) commitText();
                                        isDrawing.current = true;
                                        startPos.current = { x, y };
                                        snapshotRef.current = canvasRef.current!.getContext('2d')!.getImageData(0,0, canvasRef.current!.width, canvasRef.current!.height);
                                    }}
                                    onPointerMove={(e) => {
                                        if(!isDrawing.current || editTool === 'text') return;
                                        e.preventDefault();
                                        const ctx = canvasRef.current!.getContext('2d')!;
                                        const rect = canvasRef.current!.getBoundingClientRect();
                                        const scaleX = canvasRef.current!.width / rect.width;
                                        const scaleY = canvasRef.current!.height / rect.height;
                                        const cur = { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
                                        if(editTool === 'pen') {
                                            ctx.strokeStyle = editColor; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(startPos.current.x, startPos.current.y); ctx.lineTo(cur.x, cur.y); ctx.stroke(); startPos.current = cur;
                                        }
                                        else if(editTool === 'rect') {
                                            ctx.putImageData(snapshotRef.current!, 0, 0); ctx.strokeStyle = editColor; ctx.lineWidth = 4; ctx.strokeRect(startPos.current.x, startPos.current.y, cur.x - startPos.current.x, cur.y - startPos.current.y);
                                        }
                                    }}
                                    onPointerUp={() => { if (editTool === 'text') return; isDrawing.current = false; saveToEditHistory(); }}
                                    onPointerCancel={() => { isDrawing.current = false; }}
                                />
                                {textInput.visible && (
                                    <input ref={textInputRef} type="text" value={textInput.text} onChange={e => setTextInput({ ...textInput, text: e.target.value })} onMouseDown={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Enter') commitText(); }} className="absolute z-[10002] bg-black/80 outline-none border-2 border-dashed border-[#E6C299] px-2 py-1 placeholder-white/50 shadow-2xl rounded" placeholder="Hier tippen + Enter..." style={{ left: textInput.x, top: textInput.y - 12, color: editColor, font: 'bold 24px sans-serif', minWidth: '250px' }} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- HAUPT DOCK: immer sichtbar — mobil Wisch-Scroll ohne sichtbaren Scrollbalken; Desktop nie overflow --- */}
            <div className="w-full md:w-auto max-w-full overflow-x-auto md:overflow-x-visible overscroll-x-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className={`pe-productivity-dock flex items-center gap-1 md:gap-2 carbon-panel-dock carbon-weave-dock px-2 sm:px-3 md:px-5 py-1.5 min-h-12 md:min-h-[3.25rem] h-auto rounded-xl border border-[#20232b] bg-[#0a0b0e]/95 shadow-[0_10px_35px_rgba(0,0,0,0.6)] flex-shrink-0 w-max min-w-full md:min-w-0 md:w-auto justify-between md:justify-start overflow-visible`}>
                
                {!isAdminView && (
                <div className="hidden lg:flex items-center gap-5 mr-3 border-r border-[#22252e] pr-5 relative flex-shrink-0 min-w-0 carbon-content">
                    <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
                        <div className="flex flex-col items-start justify-center flex-shrink-0 min-w-0 gap-0.5">
                            <span className="text-[8px] text-slate-500 font-bold tracking-widest uppercase leading-none">
                                Mandant-Nummer
                            </span>
                            <MandantNrPill
                                displayNr={displayMandantNr}
                                betreuer={activeBetreuer}
                                team={team}
                                tarifLabel={tarifBadge?.label}
                                tarifClassName={
                                    tarifBadge
                                        ? `px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest border ${TARIF_BADGE_CLASS[tarifBadge.status]}`
                                        : undefined
                                }
                                showNotification={showNotification}
                                size="sm"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col items-end justify-center flex-shrink-0 pl-1 gap-0.5">
                        <span className="text-[8px] text-slate-500 font-bold tracking-widest uppercase leading-none">Jahr</span>
                        <span className="text-base text-[#E6C299] led-gold font-black font-mono-numbers tracking-tight leading-none">
                            {fiscalYear}
                        </span>
                    </div>
                </div>
                )}

                <ToolbarButton icon={Undo2} label="Zurück" onClick={() => undo()} disabled={!past || past.length === 0} />
                <ToolbarButton icon={Redo2} label="Vor" onClick={() => redo()} disabled={!future || future.length === 0} />
                
                <div className="w-px h-8 bg-[#22252e] mx-0.5 md:mx-2 relative z-10 shrink-0" />
                
                <ToolbarButton
                    icon={CalculatorIcon}
                    label="Rechner"
                    onClick={() => {
                        engage();
                        setActiveTool(activeTool === 'calculator' ? null : 'calculator');
                    }}
                    isActive={activeTool === 'calculator'}
                />
                
                <div className="w-px h-8 bg-[#22252e] mx-0.5 md:mx-2 relative z-10 shrink-0" />
                
                <ToolbarButton icon={Printer} label="Gesamt" onClick={() => {
                    engage();
                    const style = document.createElement('style');
                    style.id = 'pe-temp-portrait-print';
                    style.textContent = '@page { size: A4 portrait !important; margin: 1cm !important; }';
                    document.head.appendChild(style);
                    const cleanup = () => {
                        style.remove();
                        window.removeEventListener('afterprint', cleanup);
                    };
                    window.addEventListener('afterprint', cleanup);
                    window.print();
                    // Fallback falls afterprint ausbleibt
                    setTimeout(cleanup, 60_000);
                }} />
                <ToolbarButton icon={Scissors} label="Teil" onClick={() => startSelection('print')} isActive={photoStep !== 'idle' && selectionType === 'print'} />
                <ToolbarButton icon={Camera} label="Foto" onClick={() => startSelection('photo')} isActive={photoStep !== 'idle' && selectionType === 'photo'} />
            </div>
            </div>

            {/* Admin-Tools: mobil unter dem Dock, ab md inline */}
            <div className={`admin-dock-row flex gap-2 mr-1 flex-shrink-0 justify-center md:justify-end flex-wrap ${isAdminView ? '' : 'mt-2'}`}>
                 
                 {/* VERSTECKTER PREIS-BUTTON */}
                 <MiniAdminButton 
                     icon={isAdminUnlocked ? ExternalLink : Lock} 
                     label="Preise" 
                     color={isAdminUnlocked ? "text-[#E6C299] led-gold" : "text-red-500/70"} 
                     onClick={() => { 
                         engage();
                         if(isAdminUnlocked) { 
                             window.open('https://www.friseur-coaching.de/tools/Preisübersicht.html', '_blank'); 
                         } else { 
                             setPendingAction('preise'); 
                             setActiveTool('auth'); 
                         } 
                     }} 
                     isActive={false} 
                 />

                 <MiniAdminButton 
                     icon={isAdminUnlocked || showFormulas ? FunctionIcon : Lock} 
                     label="Formeln" 
                     onClick={() => {
                         engage();
                         if (showFormulas) { setShowFormulas(false); } else if (isAdminUnlocked) { setShowFormulas(true); } else { setPendingAction('formulas'); setActiveTool('auth'); }
                     }} 
                     isActive={showFormulas} 
                     color={showFormulas ? "text-[#E6C299] led-gold" : (isAdminUnlocked ? "text-slate-400" : "text-red-500/70")} 
                 />
                 
                 <MiniAdminButton 
                     icon={isAdminUnlocked ? History : Lock} 
                     label="Backup" 
                     color={isAdminUnlocked ? "text-[#E6C299] led-gold" : "text-red-500/70"} 
                     onClick={() => {
                         engage();
                         if(isAdminUnlocked) { setActiveTool(activeTool === 'history' ? null : 'history') } else { setPendingAction('history'); setActiveTool('auth'); }
                     }} 
                     isActive={activeTool === 'auth' || activeTool === 'history'} 
                 />
            </div>

            {/* MODALS */}
            {activeTool === 'auth' && (
                <div className="absolute top-full right-0 mt-2 bg-black border border-[#22252e] carbon-panel carbon-weave-bg p-6 rounded-xl shadow-2xl w-80 z-[9999]">
                    <div className="flex justify-between text-[#E6C299] led-gold mb-4 font-bold text-sm items-center border-b border-[#22252e] pb-3 carbon-content"><span>SICHERHEIT</span><button onClick={()=>setActiveTool(null)}><X size={16}/></button></div>
                    <div className="carbon-content">
                        <DockInput value={authPin} onChange={setAuthPin} onKeyDown={(e: any) => e.key === 'Enter' && handleUnlock()} type="password" placeholder="• • • •" autoFocus />
                        {authError && <div className="text-red-400 text-[10px] text-center mt-2 font-bold led-red">ZUGRIFF VERWEIGERT</div>}
                        <button onClick={handleUnlock} className="w-full bg-[#E6C299] text-slate-900 font-black py-3 rounded-lg mt-4 text-xs uppercase shadow-[0_0_15px_rgba(230,194,153,0.3)] hover:scale-[1.02] transition-transform">Entsperren</button>
                    </div>
                </div>
            )}

            {activeTool === 'history' && (
                <div className="absolute top-full right-0 mt-2 bg-black border border-[#22252e] carbon-panel carbon-weave-bg p-5 rounded-xl shadow-2xl w-96 z-[9999]">
                    <div className="flex justify-between text-[#E6C299] led-gold mb-4 font-bold text-sm border-b border-[#22252e] pb-3 carbon-content"><span>ZEITREISE</span><button onClick={closeHistoryPanel}><X size={16}/></button></div>
                    <div className="carbon-content space-y-4">
                        {!pendingRestoreId && (
                            <button onClick={() => createSnapshot(`Backup ${new Date().toLocaleTimeString()}`)} className="w-full py-2 bg-blue-950/40 border border-blue-500/40 hover:bg-blue-950/60 text-blue-300 rounded text-xs font-bold uppercase transition-all shadow-[0_0_10px_rgba(59,130,246,0.2)]">+ Manuelles Backup</button>
                        )}
                        {pendingRestoreId ? (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-300 leading-relaxed">
                                    Willst du diesen Speicherstand wirklich wiederherstellen? Dies überschreibt die aktuellen Daten.
                                </p>
                                <DockInput
                                    value={restorePin}
                                    onChange={setRestorePin}
                                    onKeyDown={(e: any) => e.key === 'Enter' && handleConfirmRestore()}
                                    type="password"
                                    placeholder="• • • •"
                                    autoFocus
                                />
                                {restorePinError && (
                                    <div className="text-red-400 text-[10px] text-center font-bold led-red">ZUGRIFF VERWEIGERT</div>
                                )}
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={cancelRestoreConfirm}
                                        className="flex-1 text-xs font-bold text-slate-400 uppercase hover:text-white transition-colors py-3 rounded-lg border border-[#22252e]"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmRestore}
                                        className="flex-1 bg-[#E6C299] text-slate-900 font-black py-3 rounded-lg text-xs uppercase shadow-[0_0_15px_rgba(230,194,153,0.3)] hover:scale-[1.02] transition-transform"
                                    >
                                        Wiederherstellen
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="max-h-72 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                                {savedSnapshots.map((snap:any) => (
                                    <div key={snap.id} className="group p-3 bg-white/5 hover:bg-slate-800/40 rounded flex justify-between items-center cursor-pointer border border-[#22252e] hover:border-[#E6C299]/40 transition-all">
                                        <div onClick={() => setPendingRestoreId(snap.id)}>
                                            <div className="text-white text-sm font-bold group-hover:text-[#E6C299] transition-colors">{snap.name}</div>
                                            <div className="text-slate-400 text-xs font-mono-numbers">{new Date(snap.timestamp).toLocaleString()}</div>
                                        </div>
                                        <button onClick={() => deleteSnapshot(snap.id)} className="p-2 text-slate-500 hover:text-red-400 transition-colors"><Trash2 size={14}/></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTool === 'calculator' && (
                <SystemCalculator onClose={() => { setCalcPos({ x: 0, y: 0 }); setActiveTool(null); }} onDragStart={handleCalcDragStart} position={calcPos} />
            )}
        </div>
    );
}